// The three SVG charts on /research/problems/stats, drawn from the JSON blob
// the ERB embeds (#problem-stats-data). Hand-rolled SVG rather than a chart
// library: three fixed charts don't earn a dependency, and the page's table
// views already carry the data for anyone without JS.
//
// All three plot one series (AI solves), so one hue does all the work.
// If a second series ever returns here, #eb6834 and #1baf7a complete a
// validated colorblind-safe triple with the blue — re-check any other set.

type AiSolve = { date: string; title: string; path: string }
type Dot = {
  title: string
  path: string
  years: number
  significance: number
  date: string
}
type FrontierDot = {
  title: string
  path: string
  difficulty: number
  significance: number
  date: string
}
type StatsData = { aiSolves: AiSolve[]; dots: Dot[]; frontier: FrontierDot[] }

const INK = "#0f172a"
const INK_MUTED = "#64748b"
const GRID = "#e7eff8"
const AXIS = "#dde6f1"
const SURFACE = "#ffffff"

const SERIES_COLOR = "#2a78d6"

// Left edge of the cumulative AI-solves axis. Earlier solves still count
// toward the running total; they just aren't given their own stretch of a
// chart that is really about the last year or so.
//
// Local midnight, not Date.UTC: the tick labels are formatted in local time,
// so a UTC boundary renders as the previous month anywhere west of Greenwich.
const AI_SOLVES_AXIS_START = new Date(2025, 3, 1).getTime()

const SIGNIFICANCE_WORDS: Record<number, string> = {
  1: "Minor",
  2: "Modest",
  3: "Notable",
  4: "Major",
  5: "Historic",
}

const DIFFICULTY_WORDS: Record<number, string> = {
  1: "Approachable",
  2: "Hard",
  3: "Formidable",
  4: "Notorious",
  5: "Generational",
}

// Significance drives dot *area*, not radius — a Historic dot covers five
// times a Minor one, which is the ratio the reader is meant to see. Radius
// alone would show it as twenty-five times.
function significanceRadius(significance: number): number {
  return 4 * Math.sqrt(significance)
}

const SVG_NS = "http://www.w3.org/2000/svg"

function svgEl<K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, name)
  for (const [key, value] of Object.entries(attrs)) el.setAttribute(key, String(value))
  return el
}

function svgText(content: string, attrs: Record<string, string | number>): SVGTextElement {
  const el = svgEl("text", { "font-size": 12, fill: INK_MUTED, ...attrs })
  el.textContent = content
  return el
}

// Clean axis steps: 1/2/5 × 10^n covering roughly `count` ticks.
function niceStep(span: number, count: number): number {
  const raw = span / Math.max(count, 1)
  const power = Math.pow(10, Math.floor(Math.log10(raw)))
  for (const mult of [1, 2, 5, 10]) {
    if (raw <= mult * power) return mult * power
  }
  return 10 * power
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number)
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

// Deterministic per-title jitter so dots keep their spot across renders.
function hashJitter(text: string): number {
  let hash = 0
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) | 0
  return ((hash % 1000) + 1000) % 1000 / 1000 - 0.5 // -0.5..0.5
}

function makeTooltip(container: HTMLElement): HTMLElement {
  const tip = document.createElement("div")
  tip.className = "problem-stats__tooltip"
  container.appendChild(tip)
  return tip
}

// Fill the tooltip from [strong value, ...detail lines] via textContent —
// titles are editor data, not markup.
function fillTooltip(tip: HTMLElement, value: string, title: string | null, lines: string[]): void {
  tip.replaceChildren()
  const strong = document.createElement("span")
  strong.className = "problem-stats__tooltip-value"
  strong.textContent = value
  tip.appendChild(strong)
  if (title) {
    const t = document.createElement("span")
    t.className = "problem-stats__tooltip-title"
    t.textContent = title
    tip.appendChild(t)
  }
  for (const line of lines) {
    const div = document.createElement("div")
    div.textContent = line
    tip.appendChild(div)
  }
}

function placeTooltip(tip: HTMLElement, container: HTMLElement, x: number, y: number): void {
  const width = tip.offsetWidth
  const bounds = container.clientWidth
  let left = x + 14
  if (left + width > bounds - 4) left = x - width - 14
  tip.style.left = `${Math.max(4, left)}px`
  tip.style.top = `${Math.max(4, y - tip.offsetHeight - 10)}px`
  tip.setAttribute("data-on", "")
}

function hideTooltip(tip: HTMLElement): void {
  tip.removeAttribute("data-on")
}

// ---------- Chart A: cumulative AI solves over time ----------

function renderAiSolvesChart(container: HTMLElement, solves: AiSolve[]): void {
  container.replaceChildren()
  if (solves.length === 0) return

  // One point per resolution day, cumulative count after that day.
  const byDate = new Map<string, AiSolve[]>()
  for (const s of solves) {
    const list = byDate.get(s.date) ?? []
    list.push(s)
    byDate.set(s.date, list)
  }
  const days = [...byDate.keys()].sort()
  let running = 0
  const allPoints = days.map((date) => {
    running += byDate.get(date)!.length
    return { date, time: new Date(date).getTime(), count: running, titles: byDate.get(date)! }
  })

  // AI solves are overwhelmingly recent, and anchoring the axis at the very
  // first one spends most of the plot on a flat line through years where
  // nothing happened. Clip to the window above and carry the earlier count
  // in at the left edge, so the running total still reconciles with the
  // headline number. If everything predates the window, show it all rather
  // than an empty chart.
  const clipped = allPoints.filter((p) => p.time >= AI_SOLVES_AXIS_START)
  const points = clipped.length > 0 ? clipped : allPoints
  // Cumulative count entering the window — zero when nothing was clipped,
  // since the first point's own solves are all it counts.
  const carriedIn = points[0].count - points[0].titles.length

  const width = Math.max(container.clientWidth, 320)
  const height = Math.min(Math.max(Math.round(width * 0.42), 300), 420)
  const margin = { top: 18, right: 84, bottom: 36, left: 40 }
  const plotW = width - margin.left - margin.right
  const plotH = height - margin.top - margin.bottom

  const now = Date.now()
  let t0 = clipped.length > 0 ? AI_SOLVES_AXIS_START : allPoints[0].time
  const t1 = Math.max(now, points[points.length - 1].time)
  const minSpan = 1000 * 60 * 60 * 24 * 90
  if (t1 - t0 < minSpan) t0 = t1 - minSpan

  const total = points[points.length - 1].count
  // Counts tick on whole numbers — a 0.5 gridline on "problems solved" is
  // nonsense.
  const yStep = Math.max(1, niceStep(total, 4))
  const yMax = Math.max(yStep * Math.ceil(total / yStep), yStep)

  const x = (t: number) => margin.left + ((t - t0) / (t1 - t0)) * plotW
  const y = (c: number) => margin.top + plotH * (1 - c / yMax)

  const svg = svgEl("svg", { viewBox: `0 0 ${width} ${height}`, width, height })
  svg.setAttribute("aria-hidden", "true")

  // Horizontal gridlines + y ticks, hairline and solid.
  for (let value = 0; value <= yMax; value += yStep) {
    svg.appendChild(
      svgEl("line", {
        x1: margin.left, x2: width - margin.right, y1: y(value), y2: y(value),
        stroke: value === 0 ? AXIS : GRID, "stroke-width": 1,
      }),
    )
    if (value > 0) {
      svg.appendChild(
        svgText(String(value), { x: margin.left - 8, y: y(value) + 4, "text-anchor": "end" }),
      )
    }
  }

  // X ticks: ~5 evenly spaced dates. Coarse labels (bare years) can repeat
  // across evenly spaced ticks, so identical neighbors are dropped.
  const spanDays = (t1 - t0) / (1000 * 60 * 60 * 24)
  const tickCount = Math.min(6, Math.max(3, Math.floor(plotW / 130)))
  let previousLabel = ""
  for (let i = 0; i <= tickCount; i++) {
    const t = t0 + ((t1 - t0) * i) / tickCount
    const d = new Date(t)
    const label =
      spanDays > 1200
        ? String(d.getFullYear())
        : d.toLocaleDateString("en-US", { month: "short", year: "numeric" })
    if (label === previousLabel) continue
    previousLabel = label
    svg.appendChild(
      svgText(label, { x: x(t), y: height - margin.bottom + 20, "text-anchor": "middle" }),
    )
  }

  // Step-after path from the left edge, extended to now. It enters at
  // carriedIn rather than zero so a clipped window doesn't restart the count.
  let d = `M ${x(t0)} ${y(carriedIn)}`
  for (const p of points) d += ` L ${x(p.time)} ${y(p.count - p.titles.length)} L ${x(p.time)} ${y(p.count)}`
  d += ` L ${x(t1)} ${y(total)}`

  svg.appendChild(
    svgEl("path", {
      // Down to the baseline, back along it, and closed — an implicit Z from
      // the right edge would cut diagonally to a non-zero start.
      d: `${d} L ${x(t1)} ${y(0)} L ${x(t0)} ${y(0)} Z`,
      fill: SERIES_COLOR, "fill-opacity": 0.1, stroke: "none",
    }),
  )
  svg.appendChild(
    svgEl("path", {
      d, fill: "none", stroke: SERIES_COLOR, "stroke-width": 2,
      "stroke-linecap": "round", "stroke-linejoin": "round",
    }),
  )

  // End marker + direct label with the running total.
  svg.appendChild(
    svgEl("circle", {
      cx: x(t1), cy: y(total), r: 4.5,
      fill: SERIES_COLOR, stroke: SURFACE, "stroke-width": 2,
    }),
  )
  svg.appendChild(
    svgText(`${total} solved`, {
      x: x(t1) + 10, y: y(total) + 4, fill: INK, "font-weight": 600,
    }),
  )

  // Crosshair + tooltip: a vertical hairline snapping to the nearest day.
  const crosshair = svgEl("line", {
    y1: margin.top, y2: height - margin.bottom,
    stroke: INK_MUTED, "stroke-width": 1, opacity: 0,
  })
  svg.appendChild(crosshair)
  const hoverDot = svgEl("circle", {
    r: 4.5, fill: SERIES_COLOR, stroke: SURFACE, "stroke-width": 2, opacity: 0,
  })
  svg.appendChild(hoverDot)

  container.appendChild(svg)
  const tip = makeTooltip(container)

  const showPoint = (index: number, pointerY: number | null) => {
    const p = points[index]
    crosshair.setAttribute("x1", String(x(p.time)))
    crosshair.setAttribute("x2", String(x(p.time)))
    crosshair.setAttribute("opacity", "0.4")
    hoverDot.setAttribute("cx", String(x(p.time)))
    hoverDot.setAttribute("cy", String(y(p.count)))
    hoverDot.setAttribute("opacity", "1")
    const named = p.titles.slice(0, 3).map((s) => s.title)
    if (p.titles.length > 3) named.push(`…and ${p.titles.length - 3} more`)
    fillTooltip(tip, `${p.count} solved by AI`, null, [formatDate(p.date), ...named])
    placeTooltip(tip, container, x(p.time), pointerY ?? y(p.count))
  }
  const hide = () => {
    crosshair.setAttribute("opacity", "0")
    hoverDot.setAttribute("opacity", "0")
    hideTooltip(tip)
  }

  const overlay = svgEl("rect", {
    x: margin.left, y: margin.top, width: plotW, height: plotH,
    fill: "transparent", tabindex: 0,
  })
  let focusIndex = points.length - 1
  overlay.addEventListener("pointermove", (event) => {
    const rect = svg.getBoundingClientRect()
    const px = ((event.clientX - rect.left) / rect.width) * width
    let nearest = 0
    let best = Infinity
    points.forEach((p, i) => {
      const dist = Math.abs(x(p.time) - px)
      if (dist < best) { best = dist; nearest = i }
    })
    focusIndex = nearest
    showPoint(nearest, ((event.clientY - rect.top) / rect.height) * height)
  })
  overlay.addEventListener("pointerleave", hide)
  // Keyboard: focus shows the latest point; arrows walk the series.
  overlay.addEventListener("focus", () => showPoint(focusIndex, null))
  overlay.addEventListener("blur", hide)
  overlay.addEventListener("keydown", (event) => {
    if (event.key === "ArrowLeft") focusIndex = Math.max(0, focusIndex - 1)
    else if (event.key === "ArrowRight") focusIndex = Math.min(points.length - 1, focusIndex + 1)
    else return
    event.preventDefault()
    showPoint(focusIndex, null)
  })
  svg.appendChild(overlay)
}

// ---------- Chart B: significance vs. years open ----------

function renderSignificanceChart(container: HTMLElement, dots: Dot[]): void {
  container.replaceChildren()
  if (dots.length === 0) return

  const width = Math.max(container.clientWidth, 320)
  const bandH = width < 560 ? 52 : 64
  const margin = { top: 10, right: 30, bottom: 44, left: 84 }
  const plotH = bandH * 5
  const height = margin.top + plotH + margin.bottom
  const plotW = width - margin.left - margin.right

  const maxYears = Math.max(...dots.map((d) => d.years), 1)
  const xStep = niceStep(maxYears, 5)
  const xMax = Math.max(xStep * Math.ceil(maxYears / xStep), xStep)

  const x = (years: number) => margin.left + (years / xMax) * plotW
  // Significance 5 (Historic) on top.
  const bandCenter = (sig: number) => margin.top + (5 - sig) * bandH + bandH / 2

  const svg = svgEl("svg", { viewBox: `0 0 ${width} ${height}`, width, height })
  svg.setAttribute("aria-hidden", "true")

  // Vertical gridlines + x ticks.
  for (let value = 0; value <= xMax; value += xStep) {
    svg.appendChild(
      svgEl("line", {
        x1: x(value), x2: x(value), y1: margin.top, y2: margin.top + plotH,
        stroke: value === 0 ? AXIS : GRID, "stroke-width": 1,
      }),
    )
    svg.appendChild(
      svgText(String(value), {
        x: x(value), y: margin.top + plotH + 18, "text-anchor": "middle",
      }),
    )
  }
  svg.appendChild(
    svgText("Years open when resolved", {
      x: margin.left + plotW / 2, y: height - 6, "text-anchor": "middle",
    }),
  )

  // Band labels: the significance words, never the numbers.
  for (let sig = 1; sig <= 5; sig++) {
    svg.appendChild(
      svgText(SIGNIFICANCE_WORDS[sig], {
        x: margin.left - 12, y: bandCenter(sig) + 4, "text-anchor": "end", fill: INK,
      }),
    )
  }

  const positioned = dots.map((dot) => ({
    dot,
    cx: x(dot.years),
    cy: bandCenter(dot.significance) + hashJitter(dot.title) * bandH * 0.55,
  }))

  for (const p of positioned) {
    svg.appendChild(
      svgEl("circle", {
        cx: p.cx, cy: p.cy, r: 5, fill: SERIES_COLOR, stroke: SURFACE, "stroke-width": 2,
      }),
    )
  }

  // One selective direct label: the longest-standing problem is the extreme
  // a reader will ask about first. Everything else lives in the tooltip.
  const oldest = positioned.reduce((a, b) => (a.dot.years >= b.dot.years ? a : b))
  const labelAnchor = oldest.cx > margin.left + plotW * 0.6 ? "end" : "start"
  svg.appendChild(
    svgText(oldest.dot.title, {
      x: oldest.cx + (labelAnchor === "end" ? -10 : 10),
      y: oldest.cy + 4,
      "text-anchor": labelAnchor,
      fill: INK,
      "font-weight": 600,
    }),
  )

  const ring = svgEl("circle", {
    r: 7, fill: "none", stroke: INK, "stroke-width": 1.5, opacity: 0,
  })
  svg.appendChild(ring)

  container.appendChild(svg)
  const tip = makeTooltip(container)

  // Nearest-point hover over the whole plot — an 8–10px dot is a pinpoint
  // nobody should have to land on.
  const overlay = svgEl("rect", {
    x: margin.left - 10, y: margin.top, width: plotW + 20, height: plotH,
    fill: "transparent", tabindex: 0, cursor: "pointer",
  })
  let focusIndex = 0
  const showDot = (index: number) => {
    const p = positioned[index]
    ring.setAttribute("cx", String(p.cx))
    ring.setAttribute("cy", String(p.cy))
    ring.setAttribute("opacity", "0.7")
    const years = p.dot.years < 1 ? "open less than a year" : `open ${Math.round(p.dot.years)} years`
    fillTooltip(tip, SIGNIFICANCE_WORDS[p.dot.significance], p.dot.title, [
      years,
      `Solved ${formatDate(p.dot.date)}`,
    ])
    placeTooltip(tip, container, p.cx, p.cy)
  }
  const nearestTo = (event: PointerEvent): number | null => {
    const rect = svg.getBoundingClientRect()
    const px = ((event.clientX - rect.left) / rect.width) * width
    const py = ((event.clientY - rect.top) / rect.height) * height
    let nearest: number | null = null
    let best = 48 * 48
    positioned.forEach((p, i) => {
      const dist = (p.cx - px) ** 2 + (p.cy - py) ** 2
      if (dist < best) { best = dist; nearest = i }
    })
    return nearest
  }
  const hide = () => {
    ring.setAttribute("opacity", "0")
    hideTooltip(tip)
  }
  overlay.addEventListener("pointermove", (event) => {
    const index = nearestTo(event)
    if (index === null) { hide(); return }
    focusIndex = index
    showDot(index)
  })
  overlay.addEventListener("pointerleave", hide)
  overlay.addEventListener("click", (event) => {
    const index = nearestTo(event)
    if (index !== null) window.location.assign(positioned[index].dot.path)
  })
  overlay.addEventListener("focus", () => showDot(focusIndex))
  overlay.addEventListener("blur", hide)
  overlay.addEventListener("keydown", (event) => {
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      focusIndex = (focusIndex - 1 + positioned.length) % positioned.length
    } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      focusIndex = (focusIndex + 1) % positioned.length
    } else if (event.key === "Enter") {
      window.location.assign(positioned[focusIndex].dot.path)
      return
    } else {
      return
    }
    event.preventDefault()
    showDot(focusIndex)
  })
  svg.appendChild(overlay)
}

// ---------- Chart C: the difficulty frontier ----------
//
// x = when it fell, y = how hard it looked, area = how big a deal it was.
// Difficulty rather than years-open on purpose: the difficulty rubric is
// anchored on failed attacks ("decades of failed attacks"), so a long-ignored
// problem sits low here, while years-open would have flattered it.

function renderFrontierChart(container: HTMLElement, dots: FrontierDot[]): void {
  container.replaceChildren()
  if (dots.length === 0) return

  const width = Math.max(container.clientWidth, 320)
  const narrow = width < 560
  const bandH = narrow ? 52 : 64
  const margin = { top: 34, right: 30, bottom: 40, left: narrow ? 76 : 96 }
  const plotH = bandH * 5
  const height = margin.top + plotH + margin.bottom
  const plotW = width - margin.left - margin.right

  const times = dots.map((d) => new Date(d.date).getTime())
  const t1 = Math.max(Date.now(), ...times)

  // Solves arrive in bursts with long dead stretches between them — one 2021
  // result, then nothing until 2026. Left alone that spends most of the width
  // on empty years and crushes the interesting part against the right edge, so
  // a dominant gap is collapsed: everything before it goes in a left gutter,
  // marked off by a break, and the axis proper covers only the live span.
  const sorted = [...new Set(times)].sort((a, b) => a - b)
  let breakAt: number | null = null
  if (sorted.length > 1) {
    let widest = 0
    for (let i = 1; i < sorted.length; i++) {
      const gap = sorted[i] - sorted[i - 1]
      if (gap > widest) { widest = gap; breakAt = sorted[i] }
    }
    // Only worth a break when the hole really is most of the picture.
    if (widest < (t1 - sorted[0]) * 0.3) breakAt = null
  }

  const gutterW = breakAt === null ? 0 : 54
  const axisW = plotW - gutterW
  let t0 = breakAt ?? sorted[0]
  // A catalogue whose solves all land in one week still needs a readable
  // axis, so the window never closes tighter than a quarter.
  const minSpan = 1000 * 60 * 60 * 24 * 90
  if (t1 - t0 < minSpan) t0 = t1 - minSpan
  // Breathing room so the first and last dots aren't clipped by the frame.
  const pad = (t1 - t0) * 0.04
  const axisLeft = margin.left + gutterW

  const x = (t: number) =>
    t < t0
      ? margin.left + gutterW / 2
      : axisLeft + ((t - t0 + pad) / (t1 - t0 + pad * 2)) * axisW
  // Difficulty 5 (Generational) on top.
  const bandCenter = (level: number) => margin.top + (5 - level) * bandH + bandH / 2

  const svg = svgEl("svg", { viewBox: `0 0 ${width} ${height}`, width, height })
  svg.setAttribute("aria-hidden", "true")

  // Band separators, so the five difficulty rows read as rows.
  for (let level = 0; level <= 5; level++) {
    const y = margin.top + level * bandH
    svg.appendChild(
      svgEl("line", {
        x1: margin.left, x2: width - margin.right, y1: y, y2: y,
        stroke: level === 5 ? AXIS : GRID, "stroke-width": 1,
      }),
    )
  }

  // The break: a hairline pair separating the gutter from the live axis, so
  // the compressed years read as a cut rather than as real distance.
  if (breakAt !== null) {
    for (const dx of [-3, 3]) {
      svg.appendChild(
        svgEl("line", {
          x1: axisLeft + dx, x2: axisLeft + dx,
          y1: margin.top + 4, y2: margin.top + plotH - 4,
          stroke: AXIS, "stroke-width": 1, "stroke-dasharray": "3 3",
        }),
      )
    }
    svg.appendChild(
      svgText("earlier", {
        x: margin.left + gutterW / 2, y: margin.top + plotH + 18,
        "text-anchor": "middle", "font-size": 11,
      }),
    )
  }

  // X ticks: dates across the bottom, coarsening to bare years on long spans.
  const spanDays = (t1 - t0) / (1000 * 60 * 60 * 24)
  const tickCount = Math.min(6, Math.max(3, Math.floor(axisW / 130)))
  let previousLabel = ""
  for (let i = 0; i <= tickCount; i++) {
    const t = t0 + ((t1 - t0) * i) / tickCount
    const d = new Date(t)
    const label =
      spanDays > 1200
        ? String(d.getFullYear())
        : d.toLocaleDateString("en-US", { month: "short", year: "numeric" })
    if (label === previousLabel) continue
    previousLabel = label
    svg.appendChild(
      svgText(label, { x: x(t), y: margin.top + plotH + 18, "text-anchor": "middle" }),
    )
  }
  svg.appendChild(
    svgText("When it was solved", {
      x: margin.left + plotW / 2, y: height - 6, "text-anchor": "middle",
    }),
  )

  // Band labels: the difficulty words, never the numbers.
  for (let level = 1; level <= 5; level++) {
    svg.appendChild(
      svgText(DIFFICULTY_WORDS[level], {
        x: margin.left - 12, y: bandCenter(level) + 4, "text-anchor": "end", fill: INK,
        "font-size": narrow ? 11 : 12,
      }),
    )
  }

  // Size key in the top margin — area is an encoding, so it needs a legend.
  const keyLevels = [1, 3, 5]
  const keyGap = 16
  const charW = 6.1
  const keyWidths = keyLevels.map(
    (s) => significanceRadius(s) * 2 + 5 + SIGNIFICANCE_WORDS[s].length * charW,
  )
  let keyX = width - margin.right - (keyWidths.reduce((a, b) => a + b, 0) + keyGap * 2)
  const keyY = margin.top - 16
  for (const [i, level] of keyLevels.entries()) {
    const r = significanceRadius(level)
    svg.appendChild(
      svgEl("circle", {
        cx: keyX + r, cy: keyY, r,
        fill: SERIES_COLOR, "fill-opacity": 0.75, stroke: SURFACE, "stroke-width": 2,
      }),
    )
    svg.appendChild(
      svgText(SIGNIFICANCE_WORDS[level], {
        x: keyX + r * 2 + 5, y: keyY + 4, "font-size": 11,
      }),
    )
    keyX += keyWidths[i] + keyGap
  }

  // Same-day solves would stack exactly, so both axes get a deterministic
  // per-title nudge — the dot keeps its spot across re-renders.
  const dayPx = axisW / Math.max(spanDays, 1)
  const jitterX = Math.min(14, Math.max(5, dayPx * 0.8))
  const positioned = dots.map((dot) => ({
    dot,
    r: significanceRadius(dot.significance),
    cx: x(new Date(dot.date).getTime()) + hashJitter(`${dot.title}|x`) * jitterX,
    cy: bandCenter(dot.difficulty) + hashJitter(dot.title) * bandH * 0.5,
  }))

  // Big dots first so a Historic mark never hides a Minor one.
  for (const p of [...positioned].sort((a, b) => b.r - a.r)) {
    svg.appendChild(
      svgEl("circle", {
        cx: p.cx, cy: p.cy, r: p.r,
        fill: SERIES_COLOR, "fill-opacity": 0.75, stroke: SURFACE, "stroke-width": 2,
      }),
    )
  }

  // No direct label here, unlike the other two charts: the dots bunch tightly
  // enough that a title of any length covers its own neighbours wherever it
  // is placed. Naming a problem is the tooltip's job on this one.

  const ring = svgEl("circle", {
    fill: "none", stroke: INK, "stroke-width": 1.5, opacity: 0,
  })
  svg.appendChild(ring)

  container.appendChild(svg)
  const tip = makeTooltip(container)

  const overlay = svgEl("rect", {
    x: margin.left - 10, y: margin.top, width: plotW + 20, height: plotH,
    fill: "transparent", tabindex: 0, cursor: "pointer",
  })
  let focusIndex = 0
  const showDot = (index: number) => {
    const p = positioned[index]
    ring.setAttribute("cx", String(p.cx))
    ring.setAttribute("cy", String(p.cy))
    ring.setAttribute("r", String(p.r + 3))
    ring.setAttribute("opacity", "0.7")
    fillTooltip(tip, DIFFICULTY_WORDS[p.dot.difficulty], p.dot.title, [
      `${SIGNIFICANCE_WORDS[p.dot.significance]} significance`,
      `Solved ${formatDate(p.dot.date)}`,
    ])
    placeTooltip(tip, container, p.cx, p.cy)
  }
  // Nearest-point rather than per-dot hover: the clumps overlap, and a dot
  // is a pinpoint nobody should have to land on.
  const nearestTo = (event: PointerEvent): number | null => {
    const rect = svg.getBoundingClientRect()
    const px = ((event.clientX - rect.left) / rect.width) * width
    const py = ((event.clientY - rect.top) / rect.height) * height
    let nearest: number | null = null
    let best = 48 * 48
    positioned.forEach((p, i) => {
      const dist = (p.cx - px) ** 2 + (p.cy - py) ** 2
      if (dist < best) { best = dist; nearest = i }
    })
    return nearest
  }
  const hide = () => {
    ring.setAttribute("opacity", "0")
    hideTooltip(tip)
  }
  overlay.addEventListener("pointermove", (event) => {
    const index = nearestTo(event)
    if (index === null) { hide(); return }
    focusIndex = index
    showDot(index)
  })
  overlay.addEventListener("pointerleave", hide)
  overlay.addEventListener("click", (event) => {
    const index = nearestTo(event)
    if (index !== null) window.location.assign(positioned[index].dot.path)
  })
  overlay.addEventListener("focus", () => showDot(focusIndex))
  overlay.addEventListener("blur", hide)
  overlay.addEventListener("keydown", (event) => {
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      focusIndex = (focusIndex - 1 + positioned.length) % positioned.length
    } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      focusIndex = (focusIndex + 1) % positioned.length
    } else if (event.key === "Enter") {
      window.location.assign(positioned[focusIndex].dot.path)
      return
    } else {
      return
    }
    event.preventDefault()
    showDot(focusIndex)
  })
  svg.appendChild(overlay)
}

// ---------- Boot ----------

export function mountProblemStats(): void {
  const dataEl = document.getElementById("problem-stats-data")
  if (!dataEl?.textContent) return
  const data = JSON.parse(dataEl.textContent) as StatsData

  const aiContainer = document.getElementById("ai-solves-chart")
  const dotContainer = document.getElementById("significance-chart")
  const frontierContainer = document.getElementById("frontier-chart")

  const render = () => {
    if (aiContainer) renderAiSolvesChart(aiContainer, data.aiSolves)
    if (dotContainer) renderSignificanceChart(dotContainer, data.dots)
    if (frontierContainer) renderFrontierChart(frontierContainer, data.frontier)
  }
  render()

  // Re-render on container resize, coalesced to one draw per frame.
  let queued = false
  const observer = new ResizeObserver(() => {
    if (queued) return
    queued = true
    requestAnimationFrame(() => {
      queued = false
      render()
    })
  })
  if (aiContainer) observer.observe(aiContainer)
}
